/**
 * Vacaciones Page — Card-based UI
 * src/pages/Vacaciones.jsx
 *
 * Card: person color bar, nombre, pago mensual, balance (red), valor total (blue).
 * Modal: Person, Nombre, Fecha Compra, Deposito, Pago Mensual, Meses Pagados, Meses Por Pagar.
 *        Balance and Valor Total shown calculated/read-only.
 *
 * Valor Total = Deposito + PagoMensual x (MesesPagados + MesesPorPagar)
 * Balance     = PagoMensual x MesesPorPagar  (remaining payments)
 */

import { useState, useEffect } from 'react';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc } from 'firebase/firestore';
import { Plus, Trash2, ChevronRight } from 'lucide-react';
import { showToast } from '../components/Toast';
import { useSettings } from '../context/SettingsContext';
import { logActivity } from '../utils/auditLog';

const mxnBadge = { fontSize: '0.7rem', background: 'rgba(16,185,129,0.12)', color: '#10b981', borderRadius: '0.35rem', padding: '0.2rem 0.5rem', fontWeight: 700, verticalAlign: 'middle', marginLeft: '0.5rem' };

const fmt = (n) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN', minimumFractionDigits: 2 }).format(n || 0);

const valorTotal = (r) =>
  (r.deposito || 0) + (r.pagoMensual || 0) * ((r.mesesPagados || 0) + (r.mesesPorPagar || 0));

const SEED = [
  { person: 'Tumi', nombre: 'Club Med Ixtapa', fechaCompra: '2022-01-15', deposito: 50000, pagoMensual: 3500, mesesPagados: 26, mesesPorPagar: 94 },
];

async function seedVacaciones(db) {
  const snap = await getDocs(collection(db, 'vacaciones'));
  if (snap.empty) {
    for (const item of SEED) await addDoc(collection(db, 'vacaciones'), item);
    const newSnap = await getDocs(collection(db, 'vacaciones'));
    return newSnap.docs.map(d => ({ id: d.id, ...d.data() }));
  }
  return snap.docs.map(d => ({ id: d.id, ...d.data() }));
}

const personColor = (p) => p === 'Tumi' ? '#8b5cf6' : '#3b82f6';

const BLANK_MODAL = {
  id: null, person: 'Tumi', nombre: '', fechaCompra: '',
  deposito: 0, pagoMensual: 0, mesesPagados: 0, mesesPorPagar: 0, saving: false,
};

export default function Vacaciones({ db, user }) {
  const { t } = useSettings();
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal]     = useState(null);

  useEffect(() => {
    (async () => {
      try { setRows(await seedVacaciones(db)); }
      catch (err) { console.error(err); }
      finally { setLoading(false); }
    })();
  }, [db]);

  const totalDeposito    = rows.reduce((s, r) => s + (r.deposito || 0), 0);
  const totalPagoMensual = rows.reduce((s, r) => s + (r.pagoMensual || 0), 0);
  const totalBalance     = rows.reduce((s, r) => s + (r.pagoMensual || 0) * (r.mesesPorPagar || 0), 0);
  const totalValorTotal  = rows.reduce((s, r) => s + valorTotal(r), 0);

  const openAdd  = () => setModal({ ...BLANK_MODAL });
  const openEdit = (row) => setModal({
    id: row.id, person: row.person, nombre: row.nombre || '',
    fechaCompra: row.fechaCompra || '', deposito: row.deposito || 0,
    pagoMensual: row.pagoMensual || 0, mesesPagados: row.mesesPagados || 0,
    mesesPorPagar: row.mesesPorPagar || 0, saving: false,
  });

  const handleSave = async () => {
    if (!modal) return;
    setModal(m => ({ ...m, saving: true }));
    try {
      const data = {
        person:       modal.person,
        nombre:       modal.nombre,
        fechaCompra:  modal.fechaCompra || '',
        deposito:     parseFloat(modal.deposito) || 0,
        pagoMensual:  parseFloat(modal.pagoMensual) || 0,
        mesesPagados: parseFloat(modal.mesesPagados) || 0,
        mesesPorPagar: parseFloat(modal.mesesPorPagar) || 0,
      };
      if (modal.id) {
        await updateDoc(doc(db, 'vacaciones', modal.id), data);
        setRows(prev => prev.map(r => r.id === modal.id ? { ...r, ...data } : r));
        await logActivity(db, user, 'editar', 'Vacación', { persona: modal.person, nombre: modal.nombre, monto: modal.deposito });
        showToast('Time Share actualizado');
      } else {
        const ref = await addDoc(collection(db, 'vacaciones'), data);
        setRows(prev => [...prev, { id: ref.id, ...data }]);
        await logActivity(db, user, 'crear', 'Vacación', { persona: modal.person, nombre: modal.nombre, monto: modal.deposito });
        showToast('Time Share agregado');
      }
      setModal(null);
    } catch (err) {
      console.error(err);
      showToast('Error al guardar', 'error');
      setModal(m => ({ ...m, saving: false }));
    }
  };

  const handleDelete = async () => {
    if (!modal?.id) return;
    try {
      await deleteDoc(doc(db, 'vacaciones', modal.id));
      setRows(prev => prev.filter(r => r.id !== modal.id));
      await logActivity(db, user, 'eliminar', 'Vacación', { persona: modal.person, nombre: modal.nombre });
      setModal(null);
      showToast('Time Share eliminado');
    } catch (err) {
      console.error(err);
      showToast('Error al eliminar', 'error');
    }
  };

  if (loading) return (
    <div className="page-container">
      <div className="app-loading" style={{ minHeight: '40vh' }}><div className="spinner" /></div>
    </div>
  );

  const inputStyle = { width: '100%', padding: '0.6rem 0.75rem', border: '1px solid var(--border-color)', borderRadius: '0.5rem', fontSize: '0.9rem', background: 'var(--bg-primary)', color: 'var(--text-primary)', boxSizing: 'border-box' };
  const labelStyle = { display: 'block', fontSize: '0.82rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: '0.375rem' };

  const modalBalance    = modal ? (parseFloat(modal.pagoMensual) || 0) * (parseFloat(modal.mesesPorPagar) || 0) : 0;
  const modalValorTotal = modal ? valorTotal({ deposito: parseFloat(modal.deposito) || 0, pagoMensual: parseFloat(modal.pagoMensual) || 0, mesesPagados: parseFloat(modal.mesesPagados) || 0, mesesPorPagar: parseFloat(modal.mesesPorPagar) || 0 }) : 0;

  return (
    <div className="page-container">
      {/* Title + Add button */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
        <h1 className="page-title" style={{ margin: 0 }}>{t('vacaciones')} <span style={mxnBadge}>MXN</span></h1>
        <button onClick={openAdd}
          style={{ display: 'flex', alignItems: 'center', gap: '0.35rem', background: '#1a472a', color: 'white', border: 'none', borderRadius: '0.5rem', padding: '0.5rem 0.875rem', fontWeight: 600, fontSize: '0.88rem', cursor: 'pointer' }}>
          <Plus size={16} /> {t('add')}
        </button>
      </div>

      {/* Summary cards */}
      <div className="summary-cards">
        <div className="summary-card">
          <div className="summary-card-label">{t('totalValue')} Time Shares</div>
          <div className="summary-card-value" style={{ color: '#3b82f6' }}>{fmt(totalValorTotal)}</div>
        </div>
        <div className="summary-card">
          <div className="summary-card-label">{t('deposit')}s</div>
          <div className="summary-card-value">{fmt(totalDeposito)}</div>
        </div>
        <div className="summary-card">
          <div className="summary-card-label">{t('monthlyPayment')}</div>
          <div className="summary-card-value">{fmt(totalPagoMensual)}</div>
        </div>
        <div className="summary-card">
          <div className="summary-card-label">{t('balance')} Restante</div>
          <div className="summary-card-value" style={{ color: '#ef4444' }}>{fmt(totalBalance)}</div>
        </div>
      </div>

      {/* Card list */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
        {rows.length === 0 && (
          <div className="card" style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-secondary)' }}>
            Sin time shares. Agrega uno con el boton +.
          </div>
        )}

        {rows.map(row => {
          const balance = (row.pagoMensual || 0) * (row.mesesPorPagar || 0);
          const vTotal  = valorTotal(row);
          return (
            <div key={row.id} onClick={() => openEdit(row)}
              style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', background: 'var(--bg-secondary)', borderRadius: '0.75rem', border: '1px solid var(--border-color)', padding: '0.75rem 1rem', cursor: 'pointer', transition: 'background 0.15s' }}>
              {/* Person color bar */}
              <div style={{ width: 4, height: 60, borderRadius: 2, background: personColor(row.person), flexShrink: 0 }} />

              {/* Center info */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.4rem', marginBottom: '0.2rem', flexWrap: 'nowrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', minWidth: 0 }}>
                    <span style={{ fontSize: '0.75rem', fontWeight: 600, color: personColor(row.person), flexShrink: 0 }}>{row.person}</span>
                    <span style={{ fontSize: '0.92rem', fontWeight: 600, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{row.nombre}</span>
                  </div>
                  {row.fechaCompra && <span style={{ fontSize: '0.7rem', color: 'var(--text-light)', flexShrink: 0 }}>{t('purchaseDate')}: {row.fechaCompra}</span>}
                </div>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', marginTop: '0.15rem', display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                  <span>{t('monthlyPayment')}: {fmt(row.pagoMensual)}</span>
                  <span style={{ color: '#10b981' }}>{t('paidMonths')}: {row.mesesPagados}</span>
                  <span style={{ color: '#f59e0b' }}>{t('remainingMonths')}: {row.mesesPorPagar}</span>
                </div>
              </div>

              {/* Right: balance + valor total */}
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>{t('balance')}</div>
                <div style={{ fontSize: '0.92rem', fontWeight: 700, color: '#ef4444' }}>{fmt(balance)}</div>
                <div style={{ fontSize: '0.72rem', color: '#3b82f6', fontWeight: 600, marginTop: '0.15rem' }}>{fmt(vTotal)}</div>
              </div>
              <ChevronRight size={16} style={{ color: 'var(--text-light)', flexShrink: 0 }} />
            </div>
          );
        })}
      </div>

      {/* Modal */}
      {modal && (
        <div className="modal-overlay" onClick={() => setModal(null)}>
          <div className="modal-card" onClick={e => e.stopPropagation()} style={{ maxWidth: 420, width: '100%' }}>
            <h3 style={{ margin: '0 0 1.25rem', color: 'var(--text-primary)', fontSize: '1.1rem' }}>
              {modal.id ? t('edit') : t('add')} Time Share
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.875rem', marginBottom: '1.25rem' }}>
              {/* Person */}
              <div>
                <label style={labelStyle}>{t('person')}</label>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  {['Tumi', 'Alberto'].map(p => (
                    <button key={p} onClick={() => setModal(m => ({ ...m, person: p }))}
                      style={{ flex: 1, padding: '0.5rem', border: `2px solid ${modal.person === p ? personColor(p) : 'var(--border-color)'}`, borderRadius: '0.5rem', background: modal.person === p ? `${personColor(p)}15` : 'var(--bg-tertiary)', color: personColor(p), fontWeight: 700, cursor: 'pointer', fontSize: '0.9rem' }}>
                      {p}
                    </button>
                  ))}
                </div>
              </div>

              {/* Nombre */}
              <div>
                <label style={labelStyle}>{t('timeshare')}</label>
                <input type="text" value={modal.nombre} onChange={e => setModal(m => ({ ...m, nombre: e.target.value }))} placeholder="Ej. Club Med Ixtapa..." style={inputStyle} />
              </div>

              {/* Fecha Compra */}
              <div>
                <label style={labelStyle}>{t('purchaseDate')}</label>
                <input type="date" value={modal.fechaCompra} onChange={e => setModal(m => ({ ...m, fechaCompra: e.target.value }))} style={inputStyle} />
              </div>

              {/* Deposito */}
              <div>
                <label style={labelStyle}>{t('deposit')}</label>
                <input type="number" inputMode="decimal" value={modal.deposito} onChange={e => setModal(m => ({ ...m, deposito: e.target.value }))} placeholder="0.00" style={{ ...inputStyle, textAlign: 'right' }} />
              </div>

              {/* Pago Mensual */}
              <div>
                <label style={labelStyle}>{t('monthlyPayment')}</label>
                <input type="number" inputMode="decimal" value={modal.pagoMensual} onChange={e => setModal(m => ({ ...m, pagoMensual: e.target.value }))} placeholder="0.00" style={{ ...inputStyle, textAlign: 'right' }} />
              </div>

              {/* Meses Pagados + Meses Por Pagar */}
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>{t('paidMonths')}</label>
                  <input type="number" inputMode="numeric" value={modal.mesesPagados} onChange={e => setModal(m => ({ ...m, mesesPagados: e.target.value }))} placeholder="0" style={{ ...inputStyle, textAlign: 'right', color: '#10b981' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>{t('remainingMonths')}</label>
                  <input type="number" inputMode="numeric" value={modal.mesesPorPagar} onChange={e => setModal(m => ({ ...m, mesesPorPagar: e.target.value }))} placeholder="0" style={{ ...inputStyle, textAlign: 'right', color: '#f59e0b' }} />
                </div>
              </div>

              {/* Balance + Valor Total (read-only) */}
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <div style={{ flex: 1, background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: '0.5rem', padding: '0.6rem 0.875rem' }}>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginBottom: '0.2rem' }}>{t('balance')} Restante</div>
                  <div style={{ fontSize: '1rem', fontWeight: 700, color: '#ef4444' }}>{fmt(modalBalance)}</div>
                </div>
                <div style={{ flex: 1, background: 'rgba(59,130,246,0.08)', border: '1px solid rgba(59,130,246,0.2)', borderRadius: '0.5rem', padding: '0.6rem 0.875rem' }}>
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginBottom: '0.2rem' }}>{t('totalValue')}</div>
                  <div style={{ fontSize: '1rem', fontWeight: 700, color: '#3b82f6' }}>{fmt(modalValorTotal)}</div>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '0.75rem' }}>
              {modal.id && (
                <button onClick={handleDelete}
                  style={{ padding: '0.7rem 1rem', border: 'none', borderRadius: '0.5rem', background: '#ef4444', color: 'white', cursor: 'pointer', fontWeight: 600, display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                  <Trash2 size={15} />{t('delete')}
                </button>
              )}
              <button onClick={() => setModal(null)}
                style={{ flex: 1, padding: '0.7rem', border: '1px solid var(--border-color)', borderRadius: '0.5rem', background: 'var(--bg-tertiary)', color: 'var(--text-primary)', cursor: 'pointer', fontWeight: 600 }}>
                {t('cancel')}
              </button>
              <button onClick={handleSave} disabled={modal.saving}
                style={{ flex: 1, padding: '0.7rem', border: 'none', borderRadius: '0.5rem', background: '#1a472a', color: 'white', cursor: modal.saving ? 'not-allowed' : 'pointer', fontWeight: 600, opacity: modal.saving ? 0.7 : 1 }}>
                {modal.saving ? t('loading') : t('save')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
